import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { BadgeCheck, CreditCard } from 'lucide-react'
import React, { useEffect } from 'react'

interface PaymentStatsWidgetProps {
  className?: string
}

const formatPercent = (value: number) => `${value.toFixed(1)}%`

export const PaymentStatsWidget: React.FC<PaymentStatsWidgetProps> = ({ className }) => {
  const { toast } = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['orderStats', 'all'],
    queryFn: () => apiClient.getOrderStats({ period: 'all' }),
  })

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to load payment stats',
        variant: 'destructive',
      })
    }
  }, [error, toast])

  const stats = data?.data
  const paid = stats?.total_paid ?? 0
  const pending = stats?.by_payment_status?.pending ?? 0
  const review = stats?.by_payment_status?.review ?? 0
  const refunded = stats?.by_payment_status?.refunded ?? 0
  const failed = stats?.by_payment_status?.failed ?? 0
  const completionRate = stats && stats.total_orders > 0 ? (paid / stats.total_orders) * 100 : 0
  const totalOrders = stats?.total_orders ?? 0

  const secondaryMetrics = [
    {
      label: 'Awaiting Payment',
      value: pending,
      tone: 'text-amber-700 bg-amber-50 border-amber-200',
    },
    { label: 'Payment Review', value: review, tone: 'text-slate-700 bg-slate-50 border-slate-200' },
    {
      label: 'Refunded',
      value: refunded,
      tone: 'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200',
    },
    { label: 'Failed', value: failed, tone: 'text-rose-700 bg-rose-50 border-rose-200' },
  ]

  const paymentMix = [
    {
      label: 'Paid',
      value: paid,
      tone: 'bg-emerald-600',
    },
    {
      label: 'Pending / Review',
      value: pending + review,
      tone: 'bg-amber-500',
    },
    {
      label: 'Failed / Refunded',
      value: failed + refunded,
      tone: 'bg-rose-500',
    },
  ]

  return (
    <Card
      className={cn(
        'flex h-full flex-col rounded-3xl border-slate-200/80 bg-white shadow-sm',
        className
      )}
    >
      <CardHeader className="border-b border-slate-100 pb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-950">Payment Health</CardTitle>
            <CardDescription className="mt-1 text-sm text-slate-500">
              A quick look at payment completion and outstanding order payments.
            </CardDescription>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-2.5 text-emerald-700">
            <CreditCard className="h-5 w-5" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 p-4">
        {isLoading ? (
          <>
            <Skeleton className="h-24 rounded-2xl" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
            </div>
            <Skeleton className="h-28 rounded-2xl" />
          </>
        ) : stats ? (
          <>
            <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50/70 p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                    Completion Rate
                  </div>
                  <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                    {formatPercent(completionRate)}
                  </div>
                </div>
                <div className="rounded-2xl bg-white/70 p-2 text-emerald-700 shadow-sm">
                  <BadgeCheck className="h-5 w-5" />
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-600">
                {paid} paid orders out of {stats.total_orders} total orders.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {secondaryMetrics.map((metric) => (
                <div key={metric.label} className={cn('rounded-2xl border px-4 py-3', metric.tone)}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] opacity-80">
                    {metric.label}
                  </div>
                  <div className="mt-3 text-2xl font-semibold tracking-tight">{metric.value}</div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <div className="text-sm font-semibold text-slate-900">Payment Mix</div>
              <div className="mt-4 space-y-3">
                {paymentMix.map((row) => {
                  const percentage = totalOrders > 0 ? (row.value / totalOrders) * 100 : 0
                  return (
                    <div key={row.label}>
                      <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                        <span className="font-medium">{row.label}</span>
                        <span>{row.value}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                        <div
                          className={cn('h-full rounded-full', row.tone)}
                          style={{ width: `${Math.max(percentage, row.value > 0 ? 6 : 0)}%` }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}
